import {
  ConfidenceLevel,
  DemandPreset,
  SellThroughPreset,
  StrategyRow,
} from './StrategyCalculatorPanel.types';

/** Preset values — ordered for the UI pill strip */
export const DEMAND_VALUES: DemandPreset[] = [
  DemandPreset.Weak,
  DemandPreset.BelowAvg,
  DemandPreset.Typical,
  DemandPreset.AboveAvg,
  DemandPreset.Hot,
];

export const SELL_THROUGH_VALUES: SellThroughPreset[] = [
  SellThroughPreset.Weak,
  SellThroughPreset.BelowAvg,
  SellThroughPreset.Typical,
  SellThroughPreset.AboveAvg,
  SellThroughPreset.Hot,
];

/** Numeric intensity for each preset level (0 = weakest, 4 = strongest) */
const PRESET_SCORE: Record<string, number> = {
  weak: 0,
  belowAvg: 1,
  typical: 2,
  aboveAvg: 3,
  hot: 4,
};

/** Strategy IDs ordered from conservative → aggressive */
export const STRATEGY_IDS = [
  'holdForBetterPrices',
  'steadyHarvest',
  'measuredPush',
  'accelerate',
  'turboBoost',
] as const;

/**
 * Ad cost / net revenue estimates per strategy, keyed by intensity bucket.
 * Bucket 0 = low demand+sales, 2 = mid, 4 = high.
 *
 * NOTE: Values are hardcoded in USD. If multi-currency support is needed,
 * these should be replaced with locale-aware formatting via useLocalizationContext.
 */
const STRATEGY_ESTIMATES: Record<
  string,
  { costs: [string, string][]; confidence: ConfidenceLevel }
> = {
  holdForBetterPrices: {
    costs: [
      ['$1', '$0-$18'],
      ['$2', '$0-$24'],
      ['$2', '$0-$30'],
      ['$3', '$2-$36'],
      ['$4', '$5-$42'],
    ],
    confidence: 'high',
  },
  steadyHarvest: {
    costs: [
      ['$2', '$0-$20'],
      ['$3', '$0-$26'],
      ['$4', '$0-$31'],
      ['$5', '$4-$38'],
      ['$6', '$8-$45'],
    ],
    confidence: 'high',
  },
  measuredPush: {
    costs: [
      ['$5', '$0-$22'],
      ['$6', '$0-$28'],
      ['$8', '$0-$32'],
      ['$10', '$6-$40'],
      ['$12', '$10-$48'],
    ],
    confidence: 'high',
  },
  accelerate: {
    costs: [
      ['$12', '$0-$30'],
      ['$16', '$4-$36'],
      ['$20', '$8-$42'],
      ['$24', '$12-$50'],
      ['$28', '$18-$58'],
    ],
    confidence: 'high',
  },
  turboBoost: {
    costs: [
      ['$25', '$0-$38'],
      ['$32', '$0-$46'],
      ['$40', '$2-$56'],
      ['$48', '$8-$64'],
      ['$55', '$15-$72'],
    ],
    confidence: 'medium',
  },
};

/**
 * Derive strategy rows from the current preset selections.
 * The recommended strategy shifts based on the average intensity:
 *   0-1 → Hold, 2-3 → Steady, 4-5 → Measured, 6-7 → Accelerate, 8 → Turbo
 */
export const getStrategyRows = (
  demand: DemandPreset,
  sellThrough: SellThroughPreset,
  labels: Record<string, string>
): StrategyRow[] => {
  const score = PRESET_SCORE[demand] + PRESET_SCORE[sellThrough];
  const recommendedIdx = Math.min(4, Math.floor(score / 2));
  const recommendedId = STRATEGY_IDS[recommendedIdx];
  const costBucket = Math.min(4, Math.floor(score / 2));

  return STRATEGY_IDS.map((id) => {
    const est = STRATEGY_ESTIMATES[id];
    const [adCost, netRevenue] = est.costs[costBucket];
    return {
      id,
      label: labels[id] ?? id,
      adCost,
      netRevenue,
      confidence: est.confidence,
      isRecommended: id === recommendedId,
    };
  });
};

/** Default event date used when no real event context is provided (30 days from now) */
export const getDefaultEventDate = (): Date => {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d;
};

/** Max days the preset JSON covers — cap the graph window here */
export const MAX_PRESET_DAYS = 180;

/** Compute the number of days from today to the event date (min 0, max MAX_PRESET_DAYS) */
export const computeDaysToEvent = (eventDate: Date): number => {
  const now = new Date();
  const diffMs = eventDate.getTime() - now.getTime();
  return Math.min(
    MAX_PRESET_DAYS,
    Math.max(0, Math.round(diffMs / (24 * 60 * 60 * 1000)))
  );
};

/** Minimum downward drag distance (px) to trigger drawer close */
export const DRAG_CLOSE_THRESHOLD = 80;
